/**
 * Centralized Cloudinary delivery URL builder.
 *
 * Why this exists: in Sept 2026 the 30-day Cloudinary delivery report showed
 * 847 GB of image bandwidth, 84% of it from four multi-megabyte ORIGINAL jpgs
 * that were being served untouched (no width, no q_auto, no f_auto) as the
 * default Open Graph image and the marketing hero background.
 *
 * Rules this module enforces:
 *   - Every display URL gets a width (or height) cap, q_auto and f_auto.
 *   - Open Graph images are a fixed 1200x630 derivative with q_auto but NO
 *     f_auto. Social crawlers (facebookexternalhit, LinkedInBot, iMessage,
 *     Slack) do not all negotiate WebP/AVIF, so the delivered format must be
 *     the one implied by the public id's extension (jpg/png).
 *   - The raw original is only exposed through `originalUrl()` so that
 *     "download the original file" paths are explicit and easy to grep for.
 *
 * Usage:
 *   import { cldUrl, cldSrcset, cldOgImage, originalUrl } from '@/cloudinaryUrl'
 *   <img :src="cldUrl(logo_id, { h: 120 })">
 *   <img :src="cldUrl(id, { w: 1100 })" :srcset="cldSrcset(id, [550, 1100, 1650])" sizes="(min-width: 1024px) 50vw, 100vw">
 */

export const CLOUD_NAME = 'showman'
export const UPLOAD_BASE = `https://res.cloudinary.com/${CLOUD_NAME}/image/upload/`

/** Default General share image used by every page that does not set its own. Keep this in sync with public/*.html. */
export const DEFAULT_OG_PUBLIC_ID = 'v1758732194/General_Share_bxomol.jpg'
export const SHOW_BOOK_OG_PUBLIC_ID = 'v1758732195/Show_Book_Share_ooju8x.jpg'

/**
 * Responsive width ladder. Deliberately short so Cloudinary only ever
 * generates a handful of derivatives per asset (each variant is a stored
 * transformation and counts against the plan).
 */
export const WIDTH_LADDER = [480, 768, 1100, 1600, 1920]

// Matches a leading transformation segment like "c_scale,w_900" or "w_80".
const TRANSFORM_SEGMENT = /^[a-z]{1,3}_[^/]*$/

/**
 * Normalize any of the shapes we store or hard-code into a bare
 * "version/public_id.ext" string (no transformation segment):
 *   - "v123/folder/name.jpg"
 *   - "folder/name.jpg"
 *   - "https://res.cloudinary.com/showman/image/upload/c_scale,w_900/v123/name.jpg"
 * Returns null for non-Cloudinary absolute URLs so callers can pass them through.
 */
export function toPublicId (idOrUrl) {
  if (!idOrUrl || typeof idOrUrl !== 'string') return null
  if (!/^https?:\/\//i.test(idOrUrl)) return idOrUrl.replace(/^\/+/, '')
  const marker = '/image/upload/'
  const at = idOrUrl.indexOf(marker)
  if (at === -1 || !idOrUrl.includes('res.cloudinary.com')) return null
  let rest = idOrUrl.slice(at + marker.length)
  // Strip any existing transformation segments (there can be several, "/"-chained).
  const parts = rest.split('/')
  while (parts.length > 1 && TRANSFORM_SEGMENT.test(parts[0]) && !/^v\d+$/.test(parts[0])) parts.shift()
  rest = parts.join('/')
  return rest.split('?')[0]
}

function buildTransform ({w, h, crop, gravity, quality = 'auto', format = 'auto', ar, dpr, extra}) {
  const t = []
  if (crop) t.push(`c_${crop}`)
  else if (w && h) t.push('c_fill')
  else if (w || h) t.push('c_limit')
  if (gravity) t.push(`g_${gravity}`)
  else if (crop === 'fill' || (w && h && !crop)) t.push('g_auto')
  if (ar) t.push(`ar_${ar}`)
  if (w) t.push(`w_${w}`)
  if (h) t.push(`h_${h}`)
  if (dpr) t.push(`dpr_${dpr}`)
  if (quality) t.push(`q_${quality}`)
  if (format) t.push(`f_${format}`)
  if (extra) t.push(extra)
  return t.join(',')
}

/**
 * Optimized delivery URL for on-page display.
 *
 * @param {string} idOrUrl  public id (with or without version) or a full Cloudinary URL
 * @param {object} opts
 *   w, h        target box in CSS pixels. Default crop is c_limit (never upscales)
 *               when one dimension is given and c_fill,g_auto when both are given.
 *   crop        override crop mode ('fill', 'scale', 'fit', 'limit', 'thumb', ...)
 *   gravity     override gravity ('auto', 'face', 'center', ...)
 *   quality     default 'auto'; pass 'auto:eco' for large backgrounds, null to omit
 *   format      default 'auto'; pass null to keep the source format (e.g. OG images)
 *   dpr         e.g. 2 for retina thumbnails when not using srcset
 *   extra       raw extra transformation parameters to append (e.g. 'r_10')
 *
 * Non-Cloudinary absolute URLs are returned unchanged so callers can pass
 * through legacy/S3 assets safely.
 */
export function cldUrl (idOrUrl, opts = {}) {
  const publicId = toPublicId(idOrUrl)
  if (!publicId) return idOrUrl || ''
  const transform = buildTransform(opts)
  return `${UPLOAD_BASE}${transform ? transform + '/' : ''}${publicId}`
}

/**
 * srcset string for responsive <img>. Pass the widths you actually need;
 * defaults to WIDTH_LADDER capped at maxWidth so we never generate more than
 * five variants per asset. Pair with a `sizes` attribute.
 */
export function cldSrcset (idOrUrl, widths, opts = {}) {
  const publicId = toPublicId(idOrUrl)
  if (!publicId) return ''
  const list = (widths && widths.length ? widths : WIDTH_LADDER.filter(w => !opts.maxWidth || w <= opts.maxWidth))
  return list.map(w => `${cldUrl(publicId, {...opts, w})} ${w}w`).join(', ')
}

/**
 * Open Graph / social share derivative: 1200x630, q_auto, source format kept.
 * Facebook, LinkedIn, Twitter, iMessage, Slack, and Discord all render this
 * size natively, and jpg/png is guaranteed to be decodable by every crawler.
 */
export function cldOgImage (idOrUrl = DEFAULT_OG_PUBLIC_ID) {
  return cldUrl(idOrUrl, {w: 1200, h: 630, crop: 'fill', gravity: 'auto', quality: 'auto', format: null})
}

/**
 * Full-resolution original. Only use for intentional "download original"
 * actions, never for <img src>. Kept as a named function so raw-original usage
 * is greppable.
 */
export function originalUrl (idOrUrl) {
  const publicId = toPublicId(idOrUrl)
  if (!publicId) return idOrUrl || ''
  return `${UPLOAD_BASE}${publicId}`
}

export default {cldUrl, cldSrcset, cldOgImage, originalUrl, toPublicId, WIDTH_LADDER, DEFAULT_OG_PUBLIC_ID, SHOW_BOOK_OG_PUBLIC_ID}
